import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  appointmentTableCell,
  appointmentTableHead,
} from "../../style/FutureAppointmentStatusStyle";
import { headingStyle } from "../../style/LapPageStyle";
import rupee from "../../assets/rupee.svg";
import dayjs from "dayjs";
import { getCollectionData } from "../../connections/getApi";
import { menuItemStyle, selectField } from "../../style/AppointmentStyle";
import UseGetMedicalTestList from "../../helper/UseGetMedicalTestList";

const DailyCollectionBilling = ({ appointments = [], onClose }) => {
  const [startDate, setStartDate] = useState(
    dayjs().tz("Asia/Kolkata").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(
    dayjs().tz("Asia/Kolkata").format("YYYY-MM-DD"),
  );
  const [dailyCollection, setDailyCollection] = useState(appointments || []);
  // const handleStartDateChange = (event) => {
  //   setStartDate(event.target.value);
  // };
  const handleStartDateChange = (event) => {
    const selectedDate = event.target.value;

    const maxAllowedEnd = dayjs(selectedDate).add(30, "day");

    setStartDate(selectedDate);

    // ✅ only update endDate if it's out of range
    if (
      dayjs(endDate).isBefore(selectedDate) ||
      dayjs(endDate).isAfter(maxAllowedEnd)
    ) {
      setEndDate(selectedDate);
    }
  };
  const { data: listData } = UseGetMedicalTestList();
  const [isLoading, setIsLoading] = useState(false);

  const today = dayjs().format("YYYY-MM-DD");
  const minAllowedDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
  const [paymentType, setPaymentType] = useState("");
  const [departmentType, setDepartmentType] = useState("");

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleGetCollections = async (
    startDate,
    endDate,
    paymentType,
    departmentType,
  ) => {
    setIsLoading(true);
    try {
      const data = await getCollectionData(
        startDate,
        endDate,
        paymentType,
        departmentType,
      );
      setDailyCollection(data?.payload?.invoices);
    } catch (error) {
      console.log(error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const [totalAmt, setTotalAmt] = useState(0);

  useEffect(() => {
    handleGetCollections(startDate, endDate, paymentType, departmentType);
  }, [startDate, endDate, paymentType, departmentType]);

  useEffect(() => {
    // Calculate the total amount based on appointments
    const total = dailyCollection?.reduce((sum, appointment) => {
      return sum + (appointment?.grand_total || 0); // Summing up grand_total
    }, 0);
    setTotalAmt(total);
  }, [appointments, dailyCollection]);

  const maxEndDate = dayjs(startDate).add(30, "day").format("YYYY-MM-DD");
  // console.log(departmentType, 'departmentType');

  return (
    <Box
      sx={{
        minWidth: "60%",
        height: "90vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginTop={-1}
          marginBottom={3}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Box></Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ marginRight: "8px" }}>
              <img src={rupee} alt="" sx={{ width: "28px", height: "24px" }} />
            </Box>
            <Typography
              variant="heading1"
              fontWeight={600}
              gutterBottom
              sx={{ ...headingStyle }}
            >
              Collections
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon sx={{ color: "red" }} size="small" color="red" />
          </IconButton>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          gap={2}
          flexWrap={"wrap"}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Box sx={{ gap: 2, display: "flex" }}>
            {/* Start Date */}
            <FormControl variant="outlined" sx={{ width: "160px" }}>
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    borderRadius: "15px",
                    fontSize: "14px",
                    backgroundColor: "neutral.blueShadeBg",
                    fontWeight: "400",
                  },
                  "& fieldset": { border: "none" },
                  cursor: "pointer",
                }}
                inputProps={{
                  max: today, // only restrict future
                }}
              />
            </FormControl>

            {/* End Date */}
            <FormControl variant="outlined" sx={{ width: "160px" }}>
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    borderRadius: "15px",
                    fontSize: "14px",
                    backgroundColor: "neutral.blueShadeBg",
                    fontWeight: "400",
                  },
                  "& fieldset": { border: "none" },
                  cursor: "pointer",
                }}
                inputProps={{
                  min: startDate,
                  max: maxEndDate > today ? today : maxEndDate,
                }}
              />
            </FormControl>

            {/* Payment Type */}
            <FormControl sx={{ minWidth: "120px" }} variant="outlined">
              <InputLabel
                shrink
                id="payment-type-label"
                sx={{
                  fontSize: "16px",
                  fontFamily: "Lato",
                  color: "neutral.black",
                  "&.Mui-focused": {
                    color: "neutral.black", // 👈 focus pe bhi same
                  },
                  "&.MuiInputLabel-shrink": {
                    color: "neutral.black", // 👈 shrink state
                  },
                }}
              >
                Payment Type
              </InputLabel>

              <Select
                labelId="payment-type-label"
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                label="Payment Type"
                MenuProps={{ disablePortal: true }} // ✅ FIX
                sx={{
                  height: "40px",
                  borderRadius: "15px",
                  fontSize: "14px",
                  backgroundColor: "neutral.blueShadeBg",
                  fontWeight: "400",
                  "& fieldset": { border: "none" },
                }}
                displayEmpty
                renderValue={(selected) => (selected === "" ? "All" : selected)}
              >
                <MenuItem value="" sx={menuItemStyle}>
                  All
                </MenuItem>
                <MenuItem value="Cash" sx={menuItemStyle}>
                  Cash
                </MenuItem>
                <MenuItem value="Card" sx={menuItemStyle}>
                  Card
                </MenuItem>
                <MenuItem value="UPI" sx={menuItemStyle}>
                  UPI
                </MenuItem>
                <MenuItem value="Cheque" sx={menuItemStyle}>
                  Cheque
                </MenuItem>
              </Select>
            </FormControl>

            {/* Department Type */}
            <FormControl sx={{ minWidth: "120px" }} variant="outlined">
              <InputLabel
                shrink
                id="department-type-label"
                sx={{
                  fontSize: "16px",
                  fontFamily: "Lato",
                  color: "neutral.black",

                  "&.Mui-focused": {
                    color: "neutral.black", // 👈 focus pe bhi same
                  },
                  "&.MuiInputLabel-shrink": {
                    color: "neutral.black", // 👈 shrink state
                  },
                }}
              >
                Department
              </InputLabel>

              <Select
                labelId="department-type-label"
                value={departmentType}
                onChange={(e) => setDepartmentType(e.target.value)}
                label="Department"
                sx={{
                  height: "40px",
                  borderRadius: "15px",
                  fontSize: "14px",
                  backgroundColor: "neutral.blueShadeBg",
                  fontWeight: "400",
                  "& fieldset": { border: "none" },
                }}
                MenuProps={{ disablePortal: true }} // ✅ FIX
                displayEmpty
                renderValue={(selected) => (selected === "" ? "All" : selected)}
              >
                <MenuItem value="" sx={menuItemStyle}>
                  All
                </MenuItem>
                {listData?.payload?.departments?.map((department) => (
                  <MenuItem
                    key={department.id}
                    value={department?.name}
                    sx={menuItemStyle}
                  >
                    {department?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight={"bold"} variant="h6" mb={2}></Typography>

            <Typography
              fontWeight={"400"}
              mb={2}
              sx={{
                backgroundColor: "neutral.blueShadeBg",
                padding: "10px",
                borderRadius: "12px",
              }}
            >
              <Typography fontSize={"16px"} variant="span">
                {" "}
                Total Collections:{" "}
              </Typography>{" "}
              &#8377; {totalAmt || 0}
            </Typography>
          </Box>
        </Box>
      </Box>

      <TableContainer
        sx={{
          flex: 1, // 👈 REQUIRED
          overflowY: "auto",
          mt: 2,
        }} // 👈 only table scrolls
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={appointmentTableCell}>Sr No.</TableCell>
              <TableCell sx={appointmentTableCell}>Name</TableCell>
              <TableCell sx={appointmentTableCell}>PatientID</TableCell>
              <TableCell sx={appointmentTableCell}>Amount</TableCell>
              <TableCell sx={appointmentTableCell}>Discount</TableCell>
              <TableCell sx={appointmentTableCell}>Net</TableCell>
              <TableCell sx={appointmentTableCell}>Invoice Type</TableCell>
              <TableCell sx={appointmentTableCell}>Payment Type</TableCell>
              <TableCell sx={appointmentTableCell}>Department</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!dailyCollection && (
              <TableCell
                rowSpan={3}
                colSpan={8}
                sx={{ textAlign: "center", fontWeight: "bold" }}
              >
                No Data
              </TableCell>
            )}
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            )}
            {dailyCollection?.map((appointment, index) => {
              return (
                <TableRow
                  key={appointment.id}
                  sx={{
                    backgroundColor: "neutral.blueShadeBg",
                    borderBottom: "8px solid white",
                    borderTop: "9px solid white",
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      borderTopLeftRadius: "20px",
                      borderBottomLeftRadius: "20px",
                    }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {appointment?.name}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {appointment?.uid}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {appointment?.grand_total || 0}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {appointment?.discount || 0}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {appointment?.total || 0}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "14px", textTransform: "capitalize" }}
                  >
                    {appointment?.invoice_type || 0}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "14px", textTransform: "capitalize" }}
                  >
                    {appointment?.payment_mode || ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      borderTopRightRadius: "20px",
                      borderBottomRightRadius: "20px",
                      textTransform: "capitalize",
                    }}
                  >
                    {appointment?.department || ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DailyCollectionBilling;
